import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import usePlanFeatures from '../hooks/usePlanFeatures';
import { Plus, Pencil, Mail, UserCheck, UserX, Eye, EyeOff, X, Users } from 'lucide-react';
import { toast } from 'react-toastify';

const EMPTY_FORM = { name: '', surname: '', email: '', password: '', role: 'cashier' };

const TeamManagement = () => {
  const { user } = useAuth();
  const { t } = useTranslation('services');
  const { maxStaff } = usePlanFeatures();
  const [teamUsers, setTeamUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingActions, setLoadingActions] = useState({});

  const fetchUsers = useCallback(async () => {
    if (!user?.businessId) return;
    try {
      setLoading(true);
      const q = query(
        collection(db, 'users'),
        where('businessId', '==', user.businessId),
        where('role', 'in', ['admin', 'cashier'])
      );
      const snap = await getDocs(q);
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => u.status !== 'deleted')
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setTeamUsers(list);
    } catch (err) {
      toast.error(t('team.loadError'));
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openAddModal = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setForm({ name: u.name || '', surname: u.surname || '', email: u.emailAddress || '', password: '', role: u.role || 'cashier' });
    setFormError('');
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleFormChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    setFormError('');
    const { name, surname, email, password, role } = form;

    if (!name.trim() || !surname.trim()) {
      setFormError(t('team.formErrorNameRequired'));
      return;
    }
    if (!editingUser) {
      if (!email.trim()) { setFormError(t('team.formErrorEmailRequired')); return; }
      if (password.length < 6) { setFormError(t('team.formErrorPasswordLength')); return; }
    }

    setIsSaving(true);
    try {
      if (editingUser) {
        await updateDoc(doc(db, 'users', editingUser.id), {
          name: name.trim(),
          surname: surname.trim(),
          role,
        });
        toast.success(t('team.saveSuccess'));
      } else {
        const fn = httpsCallable(functions, 'createTeamMember');
        await fn({ name: name.trim(), surname: surname.trim(), email: email.trim(), password, role });
        toast.success(t('team.addSuccess', { name: `${name.trim()} ${surname.trim()}` }));
      }
      closeModal();
      fetchUsers();
    } catch (err) {
      if (err.code === 'functions/already-exists') {
        setFormError(t('team.emailTakenError'));
      } else {
        setFormError(err.message || t('team.genericError'));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const setActionLoading = (uid, action) =>
    setLoadingActions((prev) => ({ ...prev, [uid]: action }));
  const clearActionLoading = (uid) =>
    setLoadingActions((prev) => { const n = { ...prev }; delete n[uid]; return n; });

  const handlePasswordReset = async (u) => {
    setActionLoading(u.id, 'reset');
    try {
      await sendPasswordResetEmail(auth, u.emailAddress);
      toast.success(t('team.resetSuccess', { email: u.emailAddress }));
    } catch {
      toast.error(t('team.resetError'));
    } finally {
      clearActionLoading(u.id);
    }
  };

  const handleToggleStatus = async (u) => {
    if (u.id === user.uid) {
      toast.error(t('team.selfToggleError'));
      return;
    }
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    setActionLoading(u.id, 'toggle');
    try {
      await updateDoc(doc(db, 'users', u.id), { status: newStatus });
      toast.success(newStatus === 'active' ? t('team.toggleActiveSuccess') : t('team.toggleInactiveSuccess'));
      fetchUsers();
    } catch {
      toast.error(t('team.toggleError'));
    } finally {
      clearActionLoading(u.id);
    }
  };

  const getInitials = (u) =>
    ((u.name || '')[0] || '' + (u.surname || '')[0] || '').toUpperCase() || '?';

  const RoleBadge = ({ role }) =>
    role === 'admin'
      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">{t('team.roleBadgeAdmin')}</span>
      : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{t('team.roleBadgeCashier')}</span>;

  const StatusBadge = ({ status }) =>
    status === 'active'
      ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{t('team.statusActive')}</span>
      : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">{t('team.statusInactive')}</span>;

  const Spinner = ({ color = 'border-white' }) => (
    <span className={`inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 ${color} border-t-transparent`} />
  );

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-400">
        <Users className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm">{t('team.accessDenied')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('team.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('team.subtitle')}</p>
        </div>
        {(() => {
          const activeCount = teamUsers.filter((u) => u.status === 'active').length;
          const limitReached = maxStaff !== null && activeCount >= maxStaff;
          return (
            <div
              title={limitReached ? t('team.limitTooltip', { max: maxStaff }) : ''}
            >
              <button
                onClick={limitReached ? undefined : openAddModal}
                disabled={limitReached}
                className={`flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm ${limitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Plus className="h-4 w-4" />
                {t('team.addUserBtn')}
                {limitReached && <span className="text-xs opacity-75">({maxStaff}/{maxStaff})</span>}
              </button>
            </div>
          );
        })()}
      </div>

      {/* Role info banner */}
      <div className="mb-5 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-500 leading-relaxed">
        <span className="font-semibold text-purple-700">{t('team.roleBadgeAdmin')}</span> — {t('team.roleInfoAdmin')}
        &nbsp;·&nbsp;
        <span className="font-semibold text-blue-700">{t('team.roleBadgeCashier')}</span> — {t('team.roleInfoCashier')}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : teamUsers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
          <Users className="h-10 w-10 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">{t('team.emptyState')}</p>
          <button onClick={openAddModal} className="mt-3 text-sm text-blue-600 font-medium hover:underline">
            {t('team.addFirstUser')}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {teamUsers.map((u) => (
            <div
              key={u.id}
              className={`bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3 transition-opacity ${u.status === 'inactive' ? 'opacity-55' : ''}`}
            >
              {/* Avatar */}
              <div
                className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold text-white select-none ${u.role === 'admin' ? 'bg-purple-500' : 'bg-blue-500'}`}
              >
                {getInitials(u)}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900 text-sm">{u.name} {u.surname}</span>
                  <RoleBadge role={u.role} />
                  <StatusBadge status={u.status} />
                  {u.id === user.uid && <span className="text-xs text-gray-400">{t('team.youLabel')}</span>}
                </div>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{u.emailAddress}</p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button
                  onClick={() => openEditModal(u)}
                  title={t('team.titleEdit')}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Pencil className="h-4 w-4" />
                </button>

                <button
                  onClick={() => handlePasswordReset(u)}
                  title={t('team.titlePasswordReset')}
                  disabled={!!loadingActions[u.id]}
                  className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-40"
                >
                  {loadingActions[u.id] === 'reset'
                    ? <Spinner color="border-amber-500" />
                    : <Mail className="h-4 w-4" />}
                </button>

                <button
                  onClick={() => handleToggleStatus(u)}
                  title={u.status === 'active' ? t('team.titleToggleInactive') : t('team.titleToggleActive')}
                  disabled={!!loadingActions[u.id] || u.id === user.uid}
                  className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${
                    u.status === 'active'
                      ? 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                      : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                  }`}
                >
                  {loadingActions[u.id] === 'toggle'
                    ? <Spinner color="border-gray-400" />
                    : u.status === 'active'
                    ? <UserX className="h-4 w-4" />
                    : <UserCheck className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">
                {editingUser ? t('team.modalTitleEdit') : t('team.modalTitleNew')}
              </h2>
              <button onClick={closeModal} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-5 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('team.firstNameLabel')}</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder={t('team.firstNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('team.lastNameLabel')}</label>
                  <input
                    type="text"
                    value={form.surname}
                    onChange={(e) => handleFormChange('surname', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder={t('team.lastNamePlaceholder')}
                  />
                </div>
              </div>

              {!editingUser && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('team.emailLabel')}</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder={t('team.emailPlaceholder')}
                  />
                </div>
              )}

              {!editingUser && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('team.passwordLabel')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder={t('team.passwordPlaceholder')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{t('team.passwordHint')}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('team.roleLabel')}</label>
                <select
                  value={form.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                >
                  <option value="cashier">{t('team.roleCashier')}</option>
                  <option value="admin">{t('team.roleAdmin')}</option>
                </select>
              </div>

              {formError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {formError}
                </p>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('team.cancelBtn')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-60 flex items-center gap-2"
              >
                {isSaving && <Spinner />}
                {editingUser ? t('team.updateBtn') : t('team.saveBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
